
/**
 * PersonalDataInventory Service Layer
 * Rule: Business logic, validation, and API orchestration
 * Rule: NO UI IN SDK
 */
import { CreatePersonalDataInventorySchema, UpdatePersonalDataInventorySchema } from '@repo/contracts';
import { ApiError } from '../errors/api-error.js';

export class PersonalDataInventoryService {
    constructor(api) {
        this.api = api;
    }

    /**
     * Get all items with pagination
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    async getAll(params = {}) {
        try {
            const response = await this.api.getAll(params);

            // Map backend pagination to SDK meta format if necessary
            const pagination = response.pagination || {};
            const meta = response.meta || {
                page: pagination.page,
                limit: pagination.pageSize,
                total: pagination.total,
                pages: pagination.totalPages
            };

            return {
                success: true,
                data: response.data,
                meta: meta,
                message: response.message || 'PersonalDataInventory listesi getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch personal-data-inventory list');
        }
    }

    /**
     * Get item by ID
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async getById(id) {
        try {
            const response = await this.api.getById(id);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch personal-data-inventory');
        }
    }

    /**
     * Create new item
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async create(data) {
        const validation = CreatePersonalDataInventorySchema.safeParse(data);
        if (!validation.success) {
            throw ApiError.validation('Invalid data', validation.error);
        }

        try {
            const response = await this.api.create(validation.data);
            return {
                success: true,
                data: response.data,
                message: response.message || 'PersonalDataInventory oluşturuldu'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to create personal-data-inventory');
        }
    }

    /**
     * Update item
     * @param {number|string} id
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async update(id, data) {
        const validation = UpdatePersonalDataInventorySchema.safeParse(data);
        if (!validation.success) {
            throw ApiError.validation('Invalid data', validation.error);
        }

        try {
            const response = await this.api.update(id, validation.data);
            return {
                success: true,
                data: response.data,
                message: response.message || 'PersonalDataInventory güncellendi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update personal-data-inventory');
        }
    }

    /**
     * Delete item
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async delete(id) {
        try {
            const response = await this.api.delete(id);

            return {
                success: true,
                data: response.data,
                message: response.message || 'PersonalDataInventory silindi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete personal-data-inventory');
        }
    }

    /**
     * Batch delete items
     * @param {Array<number|string>} ids
     * @returns {Promise<Object>}
     */
    async batchDelete(ids) {
        try {
            const response = await this.api.batchDelete(ids);
            return {
                success: true,
                data: response.data,
                message: response.message || `${ids.length} öğe silindi`
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to batch delete personal-data-inventory items');
        }
    }

    async getPartyTemplate(params = {}) {
        try {
            const response = await this.api.getPartyTemplate(params);
            return {
                success: true,
                data: response.data,
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch party template');
        }
    }

    async getParty(id) {
        try {
            const response = await this.api.getParty(id);
            return {
                success: true,
                data: response.data,
                partyTemplate: response.partyTemplate ?? null,
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch party instance');
        }
    }

    async syncParty(id, payload) {
        try {
            const response = await this.api.syncParty(id, payload);
            return {
                success: true,
                data: response.data,
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to sync party');
        }
    }

    /**
     * Custom operation: createBatchDelete
     */
    async createBatchDelete(data = {}) {
        try {
            const response = await this.api.client.post(`/batch-delete`, data);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to execute createBatchDelete');
        }
    }

    /**
     * Handle and transform errors
     */
    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) {
            throw error;
        }

        if (error.statusCode || error.status) {
            throw ApiError.fromResponse(error);
        }

        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }
}
